import { PRODUCT_NAME, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Input, Segmented, Slider, Switch } from "@sagot/ui";
import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { AvatarUploader } from "./avatar-uploader";
import { BrandColorPicker } from "./brand-color-picker";
import { FontPicker } from "./font-picker";
import { LauncherIconUploader } from "./launcher-icon-uploader";
import { LauncherMark } from "./launcher-mark";
import { ControlRow } from "./setting-row";
import { WIDGET_LIMITS, type WidgetForm } from "./widget-model";

interface AppearancePanelProps {
  agentId: string;
  agentName: string;
  savedColor: string;
  serverWarning: string | null | undefined;
  brandingRemovable: boolean;
  disabled: boolean;
}

const K = "deployKnowledge.widgetStudio.appearance";

function LauncherStyleOption({ selected, label, onSelect, children, disabled }: { selected: boolean; label: string; onSelect: () => void; children: ReactNode; disabled: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-line p-3 text-left aria-pressed:border-primary aria-pressed:bg-primary-soft disabled:cursor-not-allowed"
    >
      {children}
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}

function BrandingRow({ removable, disabled }: { removable: boolean; disabled: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { control } = useFormContext<WidgetForm>();
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-line bg-surface-2 p-3.5">
      <div>
        <div className="text-sm font-semibold">{t(`${K}.branding`, { product: PRODUCT_NAME })}</div>
        <p className="mt-0.5 text-xs text-fg-subtle">
          {removable ? t(`${K}.brandingIncluded`) : t(`${K}.brandingLocked`)}{" "}
          {removable ? null : (
            <AppLink href={consoleRoutes.billing(summary.slug)} className="font-semibold text-primary-ink hover:underline">
              {t(`${K}.comparePlans`)}
            </AppLink>
          )}
        </p>
      </div>
      <Controller
        control={control}
        name="appearance.show_branding"
        render={({ field }) => (
          <Switch
            className="mt-0.5"
            checked={removable ? field.value : true}
            disabled={disabled || !removable}
            onCheckedChange={field.onChange}
            aria-label={removable ? t(`${K}.brandingSwitch`, { product: PRODUCT_NAME }) : t(`${K}.brandingSwitchLocked`, { product: PRODUCT_NAME })}
          />
        )}
      />
    </div>
  );
}

export function AppearancePanel({ agentId, agentName, savedColor, serverWarning, brandingRemovable, disabled }: AppearancePanelProps) {
  const { t } = useTranslation();
  const { control, register, watch, setValue, formState } = useFormContext<WidgetForm>();
  const color = watch("appearance.primary_color");
  const launcherStyle = watch("appearance.launcher_style");
  const launcherIconUrl = watch("appearance.launcher_icon_url");
  const launcherText = watch("appearance.launcher_label") || watch("content.title") || t(`${K}.launcherLabel`);
  const dirty = { shouldDirty: true } as const;
  return (
    <div className="space-y-6">
      <Controller
        control={control}
        name="appearance.primary_color"
        render={({ field }) => (
          <BrandColorPicker value={field.value} onChange={field.onChange} disabled={disabled} serverWarning={color === savedColor ? serverWarning : null} />
        )}
      />
      <ControlRow label={t(`${K}.theme`)} hint={t(`${K}.themeHint`)}>
        <Controller
          control={control}
          name="appearance.theme"
          render={({ field }) => (
            <Segmented
              aria-label={t(`${K}.theme`)}
              value={field.value}
              onValueChange={field.onChange}
              options={[
                { value: "light", label: t(`${K}.themeLight`) },
                { value: "dark", label: t(`${K}.themeDark`) },
                { value: "auto", label: t(`${K}.themeAuto`) },
              ]}
            />
          )}
        />
      </ControlRow>
      <Controller
        control={control}
        name="appearance.font"
        render={({ field }) => <FontPicker value={field.value} onChange={field.onChange} disabled={disabled} />}
      />
      <div>
        <span className="label">{t(`${K}.launcherStyle`)}</span>
        <div className="grid grid-cols-2 gap-2" role="group" aria-label={t(`${K}.launcherStyle`)}>
          <LauncherStyleOption selected={launcherStyle === "icon"} label={t(`${K}.launcherIcon`)} disabled={disabled} onSelect={() => { setValue("appearance.launcher_style", "icon", dirty); }}>
            <span aria-hidden="true" className="inline-flex size-9 shrink-0 items-center justify-center rounded-pill bg-primary text-on-primary">
              <LauncherMark iconUrl={launcherIconUrl} size={28} glyphClassName="size-4" />
            </span>
          </LauncherStyleOption>
          <LauncherStyleOption selected={launcherStyle === "label"} label={t(`${K}.launcherLabel`)} disabled={disabled} onSelect={() => { setValue("appearance.launcher_style", "label", dirty); }}>
            <span aria-hidden="true" className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill bg-primary px-3 text-xs font-bold text-on-primary">
              <LauncherMark iconUrl={launcherIconUrl} size={28} glyphClassName="size-4" />
              <span className="max-w-20 truncate">{launcherText}</span>
            </span>
          </LauncherStyleOption>
        </div>
        {launcherStyle === "label" ? (
          <Field className="mt-3" label={t(`${K}.launcherLabelText`)} error={formState.errors.appearance?.launcher_label?.message}>
            <Input maxLength={30} placeholder={t(`${K}.launcherLabelPlaceholder`)} {...register("appearance.launcher_label")} />
          </Field>
        ) : null}
      </div>
      <LauncherIconUploader
        agentId={agentId}
        iconUrl={launcherIconUrl}
        color={color}
        disabled={disabled}
        onChange={(url) => {
          setValue("appearance.launcher_icon_url", url, dirty);
        }}
      />
      <ControlRow label={t(`${K}.position`)}>
        <Controller
          control={control}
          name="appearance.position"
          render={({ field }) => (
            <Segmented
              aria-label={t(`${K}.position`)}
              value={field.value}
              onValueChange={field.onChange}
              options={[
                { value: "left", label: t(`${K}.positionLeft`) },
                { value: "right", label: t(`${K}.positionRight`) },
              ]}
            />
          )}
        />
      </ControlRow>
      <Controller
        control={control}
        name="appearance.radius_px"
        render={({ field }) => (
          <Slider
            label={t(`${K}.radius`)}
            value={field.value}
            onValueChange={field.onChange}
            min={WIDGET_LIMITS.radiusMin}
            max={WIDGET_LIMITS.radiusMax}
            step={WIDGET_LIMITS.radiusStep}
            formatValue={(v) => `${String(v)}px`}
            startLabel={t(`${K}.radiusSquare`)}
            endLabel={t(`${K}.radiusRound`)}
            disabled={disabled}
          />
        )}
      />
      <div className="divider" />
      <div className="space-y-4">
        <Field label={t(`${K}.headerTitle`)}>
          <Input maxLength={60} {...register("content.title")} />
        </Field>
        <Field label={t(`${K}.headerSubtitle`)}>
          <Input maxLength={80} {...register("content.subtitle")} />
        </Field>
        <AvatarUploader
          agentId={agentId}
          agentName={agentName}
          avatarUrl={watch("appearance.avatar_url")}
          useAgentAvatar={watch("appearance.use_agent_avatar")}
          disabled={disabled}
          onChange={(next) => {
            setValue("appearance.avatar_url", next.avatar_url, dirty);
            setValue("appearance.use_agent_avatar", next.use_agent_avatar, dirty);
          }}
        />
      </div>
      <BrandingRow removable={brandingRemovable} disabled={disabled} />
    </div>
  );
}
